import { FC, useEffect, useState } from 'react'
import { Image } from 'react-bootstrap'
import { radio_di_empty, radio_di_full, radio_en_empty, radio_en_full } from 'src/assets'
import { dict, fitem_t, opt, option_t } from 'src/types'
import { find_option, join_str } from 'src/utils'

import { Button } from '../button/button'

const SVG: dict<string> = {
  'di-full': radio_di_full,
  'en-full': radio_en_full,
  'di-empty': radio_di_empty,
  'en-empty': radio_en_empty,
}

interface render_t {
  idx: number
  option: option_t
  selected?: boolean
  gtm_class?: string
}

export const RadioGroup: FC<fitem_t> = ({
  id,
  label,
  required,
  disabled,
  capture = '',
  radio_type = 'DEFAULT',
  options: opts = [],
  classes = 'w-100',
  classes_label = 'm-0 p-0 font-16 c-7f7f7f',
  classes_option = 'mr-4 font-20 cursor-pointer',
  classes_icon = 'm-0 p-0 mb-1 mr-2',
  classes_disabled = 'cursor-not-allowed',
  set_field_value,
}: fitem_t) => {
  /**
   * Use State
   */
  const [selected_option, set_selected_option] = useState<opt<option_t>>({
    key: capture,
    val: find_option(opts, capture)?.val || '',
  })

  /**
   * Alias
   */
  const end = required && !disabled ? '*' : ''
  const classes_ = {
    label: join_str(
      classes_label || 'd-none',
      label ? '' : 'opacity-0',
      disabled ? 'c-7f7f7f' : ''
    ),
    option: join_str(classes_option, disabled ? classes_disabled : ''),
  }

  /**
   * Use Effect
   */
  useEffect(() => {
    set_field_value?.(id, selected_option?.key)
    if (selected_option?.gtm_class) {
      selected_option?.on_change_gtm_class?.(selected_option.gtm_class)
    }
  }, [selected_option])

  /**
   * Render functions
   */
  const render_label = () => (
    <label htmlFor={id} className={classes_.label}>
      {`${label}${end}`}
      <span className="opacity-0">-</span>
    </label>
  )

  const render_type_default = ({ idx, option, selected }: render_t) => {
    const svg_name_1 = disabled ? 'di' : 'en'
    const svg_name_2 = selected ? 'full' : 'empty'
    const svg_name = `${svg_name_1}-${svg_name_2}`
    return (
      <label key={idx} className={classes_.option}>
        <input
          type="radio"
          hidden={true}
          disabled={disabled}
          checked={selected}
          onChange={() => set_selected_option(option)}
        />
        <Image src={SVG[svg_name]} width={20} height={20} className={classes_icon} />
        {option?.val}
      </label>
    )
  }

  const render_type_squared = ({ idx, option, selected }: render_t) => (
    <Button
      key={idx}
      flavor={selected ? 'primary_flip_border' : 'grey_flip_border'}
      text={option?.val}
      on_press={() => set_selected_option(option)}
      classes="m-2 px-4 py-3"
      classes_text="font-24"
    />
  )

  const render_radio = () => {
    const sq = radio_type === 'SQUARED'
    return (
      <div className={join_str('d-flex flex-wrap', sq ? 'm-n2' : '')}>
        {opts?.map((option, idx) => {
          const selected = selected_option?.key === option?.key
          return sq
            ? render_type_squared({ idx, option, selected })
            : render_type_default({ idx, option, selected })
        })}
      </div>
    )
  }

  return (
    <main className={classes}>
      {render_label()}
      {render_radio()}
    </main>
  )
}
